// Оптимистичные правки кэша для мутаций. См. docs/data-layer.md §4.1.

import type { QueryFactory } from './createQuery'

/**
 * Одна правка кэша, которую мутация делает ДО ответа сервера: какой запрос трогаем и как
 * считаем новое значение. Руками не собирается — есть хелпер `optimisticUpdate`, он же
 * прячет единственное место, где типы запроса приходится стирать.
 */
export type OptimisticUpdate<TInput> = {
  /** Ключ затрагиваемого запроса. Зависит от входа мутации — узу правим по её id. */
  getQueryKey: (input: TInput) => unknown[]
  /** Новое значение кэша по прежнему. Прежнее гарантированно не `undefined`. */
  getNextData: (previousData: unknown, input: TInput) => unknown
}

/** Значение кэша до правки — то, к чему откатываемся, если сервер ответил ошибкой. */
export type OptimisticSnapshot = {
  queryKey: unknown[]
  previousData: unknown
}

/**
 * Описывает оптимистичную правку одного запроса.
 *
 * ```ts
 * optimistic: [
 *   optimisticUpdate(UserQuery, (user, input) => ({ ...user, forecastEnabled: input.enabled })),
 * ]
 * ```
 *
 * `previousData` внутри фабрики хранится как `unknown` (мутация ничего не знает о типах чужих
 * запросов), и приведение к типу запроса живёт здесь — ровно в одной строке, а не в каждой ручке.
 */
export function optimisticUpdate<TInput, TQueryInput, TQueryData>(
  query: QueryFactory<TQueryInput, TQueryData>,
  getNextData: (previousData: TQueryData, input: TInput) => TQueryData,
  /** Для параметризованных запросов: вход мутации → вход запроса. */
  getQueryInput?: (input: TInput) => Partial<TQueryInput>,
): OptimisticUpdate<TInput> {
  return {
    getQueryKey: (input) => query.getQueryKey(getQueryInput?.(input)),
    // Тип возвращает `query`, а `previousData` фабрика отдаёт как `unknown`: приведение
    // безопасно ровно потому, что ключ взят у того же самого запроса.
    getNextData: (previousData, input) => getNextData(previousData as TQueryData, input),
  }
}
